import React, { useEffect, useState } from "react";
import styled from "styled-components";
import InputField from "./core-components/InputField.tsx";
import { useSelector } from "react-redux";
import { translations } from "../../utils/translations.js";
import { register } from "../../services/authService.js";
import { useQuery } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";

// Styled Components
const Container = styled.div`
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
  position: absolute;
  overflow: hidden;
  width: 768px;
  max-width: 70%;
  min-height: 560px;
  top: 15%;

  @media (max-width: 768px) {
    width: 300px;
  }
`;

const FormContainer = styled.div`
  position: absolute;
  width: 63%;
  top: 0;
  height: 100%;
  transition: all 0.6s ease-in-out;
  @media (max-width: 768px) {
    position: static;
    width: 100%;
    padding-top: 20px;
  }
`;

const SignInContainer = styled.form`
  left: 0;
  height: 100%;
  width: 80%;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  width: 50%;
  height: 100%;
  overflow: hidden;

  z-index: 100;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Overlay = styled.div`
  background: linear-gradient(
    to right,
    var(--fill-green),
    var(--tertiary-green)
  );
  color: #ffffff;
  position: relative;
  left: -100%;
  height: 100%;
  width: 200%;

  @media (max-width: 768px) {
    display: none;
  }
`;

const OverlayPanel = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  top: 0;
  height: 100%;
  width: 50%;
  left: 50%;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Button = styled.button`
  border-radius: 20px;
  border: 1px solid var(--primary-green);
  background-color: var(--secondary-green);
  color: #ffffff;
  font-size: 12px;
  font-weight: bold;
  padding: 12px 45px;
  letter-spacing: 1px;
  text-transform: uppercase;

  cursor: pointer;
`;
const GoButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-brown);
  font-size: 16px;
  cursor: pointer;

  @media (min-width: 768px) {
    display: none;
  }
`;
const notifySuccess = (msg) => {
  toast.success(msg, {
    position: "top-center",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: false,
    progress: undefined,
    theme: "light",
  });
};

const notifyPassError = (msg) => {
  toast.error(msg, {
    position: "top-center",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: false,
    progress: undefined,
    theme: "light",
  });
};

const RegisterModal = () => {
  const languageCode = useSelector((state: any) => state.settings.languageCode);
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfrmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const reset = () => {
    setEmail("");
    setPassword("");
    setConfrmPassword("");
    setFirstName("");
    setLastName("");
    setPhoneNumber("");
  };
  const { data, isSuccess, isError, refetch } = useQuery({
    queryKey: ["register", email, password],
    queryFn: () =>
      register({
        lastName,
        firstName,
        phoneNumber,
        email,
        password,
      }),
    enabled: false, // Nu rulează automat
    retry: false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!passwordRegex.test(password)) {
      notifyPassError(translations[languageCode]["passwords-rule"]);
      return;
    }
    if (password !== confirmPassword) {
      notifyPassError(translations[languageCode]["passwords-not-match"]);
      return;
    }
    refetch();
  };
  useEffect(() => {
    if (isSuccess) {
      notifySuccess(translations[languageCode]["register-success"]);
      reset();
    } else if (isError) {
      console.log("Error details:", data);
      console.log("Error details:", isError);
      notifyPassError(translations[languageCode]["register-error"]);
    }
  }, [data, isError, isSuccess, languageCode]);

  return (
    <Container>
      <FormContainer>
        <SignInContainer onSubmit={handleSubmit}>
          <h1 style={{ marginTop: "-2px" }}>
            {translations[languageCode]["login-title"]}
          </h1>
          <div>
            <InputField
              name="firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              isRequired
              placeholder="John"
              label={translations[languageCode]["register-first-name"]}
            />
          </div>
          <div>
            <InputField
              name="lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              isRequired
              placeholder="Doe"
              label={translations[languageCode]["register-last-name"]}
            />
          </div>
          <div>
            <InputField
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              isRequired
              placeholder="email@example.com"
              label="Email"
            />
          </div>
          <div>
            <InputField
              name="phoneNumber"
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              isRequired
              placeholder="0712345678"
              label={translations[languageCode]["register-phone"]}
            />
          </div>
          <div>
            <InputField
              name="password"
              type={"password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              isRequired
              label="Password"
              placeholder="Pass123!@#"
            />
          </div>
          <div>
            <InputField
              name="confirmPassword"
              type={"password"}
              value={confirmPassword}
              onChange={(e) => setConfrmPassword(e.target.value)}
              isRequired
              label="Confirm password"
              placeholder="Pass123!@#"
            />
          </div>

          <Button type="submit">
            {translations[languageCode]["register-button"]}
          </Button>
          <GoButton onClick={() => (window.location.href = "/login")}>
            {translations[languageCode]["register-login-message"]}
          </GoButton>
        </SignInContainer>
      </FormContainer>

      <OverlayContainer>
        <Overlay>
          <OverlayPanel>
            <h1>Hello!</h1>

            <p>{translations[languageCode]["register-login-message"]}</p>
            <Button onClick={() => (window.location.href = "/login")}>
              {translations[languageCode]["go-login"]}
            </Button>
          </OverlayPanel>
        </Overlay>
      </OverlayContainer>
      <ToastContainer />
    </Container>
  );
};

export default RegisterModal;
